import React, { useState, useContext, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { Grid, Typography, Hidden, ArrowRightAlt, useTheme, FormControlLabel } from '@hermes/web-components'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
// eslint-disable-next-line import/no-cycle
import MobileFilterDetailsModal from '../../../search/MobileFilterDetailsModal'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { Insurer, Language, ProfileKeywordRelated } from '../../../../types'
import useSearchFiltersConfig, {
  ButtonFilterType,
  CheckboxFilterType,
  DropdownFilterType,
  FilterType
} from '../../../search/hooks/useSearchFiltersConfig'
import {
  StyledCheckbox,
  StyledInputLabel,
  StyledMenuItem,
  StyledSelect,
  StyledFilterButton,
  StyledLocationOnIcon
} from './styles'

const SearchMapModal = dynamic(() => import('../../../profiles/SearchMapModal'), {
  ssr: false
})

export type SearchFiltersProps = {
  languages: Language[]
  insurers: Insurer[]
  specialties: ProfileKeywordRelated[]
}

export type Filter = {
  onChangeFilter?: (x: any) => void
  name: string
  value: string | number
  showingCondition: boolean
  label: string
  resetOption: { value: string | number; label: string }
  options: { value: string | number; label: string }[]
}

function SearchFilters({ insurers, languages, specialties }: Readonly<SearchFiltersProps>) {
  const translate = useTranslateMessage()
  const [activeFilterName, setActiveFilterName] = useState<string | null>(null)
  const { changeFilter } = useContext(SearchProfilesPageContext)

  const theme = useTheme()

  const { showMapModal, toggleMapModal, filtersToDisplay } = useSearchFiltersConfig({
    insurers,
    languages,
    specialties
  })

  const activeFilter = useMemo(
    () => filtersToDisplay.find((filter) => filter.name === activeFilterName),
    [activeFilterName, filtersToDisplay]
  )

  return (
    <>
      <Hidden lgDown>
        <Grid item pt={{ xs: 0, lg: 0 }}>
          <Typography fontWeight="400" fontSize="15px">
            {translate('search.filters')}
          </Typography>
        </Grid>
      </Hidden>
      {filtersToDisplay.map((filter, index) => {
        switch (filter.type) {
          case FilterType.checkbox: {
            const { Icon, label, value, onChangeFilter, testId } = filter as CheckboxFilterType
            return (
              <Grid data-testid={testId} item pt="20px" key={label + index}>
                <FormControlLabel
                  sx={{
                    marginLeft: 0
                  }}
                  control={<StyledCheckbox checked={value} onChange={onChangeFilter} />}
                  label={
                    <Grid container wrap="nowrap" alignItems="center">
                      {Icon && (
                        <Icon
                          sx={{
                            width: '24px',
                            height: '24px',
                            paddingRight: '4px'
                          }}
                        />
                      )}
                      <Typography variant="body2">{label}</Typography>
                    </Grid>
                  }
                />
              </Grid>
            )
          }
          case FilterType.dropdown: {
            const { label, value, resetOption, name, options, onChangeFilter, testId } =
              filter as unknown as DropdownFilterType
            return (
              <Grid item pt={2} key={label + index}>
                <Hidden lgDown>
                  <StyledInputLabel>
                    <Typography mb={0.5} fontWeight="400" fontSize="14px" color="#2C3341">
                      {label}:
                    </Typography>
                  </StyledInputLabel>
                  <StyledSelect
                    data-testid={testId}
                    fullWidth
                    size="small"
                    value={value}
                    variant={theme.custom?.SearchFilters?.chipsVariant}
                    onChange={(ev) => {
                      if (onChangeFilter) {
                        onChangeFilter(ev.target.value as string)
                      } else {
                        changeFilter(name, ev.target.value as string)
                      }
                    }}
                    displayEmpty
                  >
                    {resetOption && (
                      <StyledMenuItem value={resetOption.value}>
                        <Typography variant="body2" textTransform="capitalize" overflow="hidden" fontSize={15}>
                          {resetOption.label}
                        </Typography>
                      </StyledMenuItem>
                    )}
                    {options?.map((option) => (
                      <StyledMenuItem key={option.label + option.value} value={option.value}>
                        <Typography variant="body2" textTransform="capitalize" overflow="hidden" fontSize={15}>
                          {option.label}
                        </Typography>
                      </StyledMenuItem>
                    ))}
                  </StyledSelect>
                </Hidden>
                <Hidden lgUp>
                  <Typography variant="body1">{label}:</Typography>
                  <Grid
                    onClick={() => setActiveFilterName(name)}
                    alignItems="center"
                    justifyContent="space-between"
                    container
                    pt={1}
                  >
                    <StyledSelect
                      data-testid={testId}
                      fullWidth
                      size="small"
                      value={value}
                      variant={theme.custom?.SearchFilters?.chipsVariant}
                      endAdornment={<ArrowRightAlt />}
                      IconComponent={() => null}
                      displayEmpty
                      readOnly
                    >
                      {resetOption && <StyledMenuItem value={resetOption.value}>{resetOption.label}</StyledMenuItem>}
                      {options?.map((option) => (
                        <StyledMenuItem key={option.label + option.value} value={option.value}>
                          <Typography variant="body2" textTransform="capitalize" overflow="hidden" fontSize={15}>
                            {option.label}
                          </Typography>
                        </StyledMenuItem>
                      ))}
                    </StyledSelect>
                  </Grid>
                </Hidden>
              </Grid>
            )
          }
          case FilterType.button: {
            const { onClick, label, testId } = filter as ButtonFilterType
            return (
              <Grid item pt={'20px'} key={label + index}>
                <StyledFilterButton data-testid={testId} variant="outlined" fullWidth onClick={onClick}>
                  <StyledLocationOnIcon color="primary" />
                  <Typography ml={1} fontWeight={500} variant="body1">
                    {label}
                  </Typography>
                </StyledFilterButton>
              </Grid>
            )
          }
          default: {
            return null
          }
        }
      })}
      <Hidden lgUp>
        {activeFilter && (
          <MobileFilterDetailsModal
            isOpen={Boolean(activeFilter)}
            onClose={() => setActiveFilterName(null)}
            // @ts-ignore
            filter={activeFilter}
            changeFilter={changeFilter}
          />
        )}
      </Hidden>
      <SearchMapModal isOpen={showMapModal} onClose={toggleMapModal} />
    </>
  )
}

export default SearchFilters
